import { ApplicationState } from "@app/modules/app.reducers";
import { schedulerActions } from "@app/modules/scheduler";
import CreateSchedulerJobForm from "@app/modules/scheduler/components/CreateSchedulerJobForm";
import {
  createStorageSelectOptions,
  jobToFormValues,
  schedulerFormValuesToJob,
} from "@app/modules/scheduler/formUtils";
import { schedulerDataSelectors } from "@app/modules/scheduler/scheduler.selectors";
import { API } from "@app/services/api/api";
import { getTranslationKey } from "@app/translations/translations.helpers";
import { DataTestIdProp, DataTestIds } from "@app/utils/dataTestIds";
import EditeableAreaFinal from "@ea/shared_components/EditeableArea/EditeableAreaFinal";
import { PlainObject } from "@ea/shared_components/redux/common.models";
import { JOB_STATUS_TYPE, SchedulerJobMapping } from "@ea/shared_types/types";
import styled from "@emotion/styled";
import { Modal } from "antd";
import { CascaderOptionType } from "antd/lib/cascader";
import * as React from "react";
import { InjectedIntlProps, injectIntl } from "react-intl";
import { ConnectedProps, connect } from "react-redux";
import { RouteComponentProps, withRouter } from "react-router";
import { Dispatch, bindActionCreators } from "redux";
import { getItsConfigurationsTableActions } from "../issueTrackingToolConfigurations/itsConfigurations.actions";
import { getItsConfigurationOptions } from "../issueTrackingToolConfigurations/itsConfigurations.selectors";
import { ITS_CONFIGURATIONS_TABLES_CONFIG } from "../issueTrackingToolConfigurations/itsConfigurations.table";
import { getSystemTableActions } from "../systemDictionary/systemDictionary.actions";
import { getSystemFormOptions } from "../systemDictionary/systemDictionary.selectors";
import { SYSTEM_DICTIONARY_TABLES_CONFIG } from "../systemDictionary/systemDictionary.table";
import { getVirtualUserPoolTableActions } from "../virtualUserPool/virtualUserPool.actions";
import { getVirtualUserPoolsFormOptions } from "../virtualUserPool/virtualUserPool.selectors";
import { VIRTUAL_USER_POOL_TABLES_CONFIG } from "../virtualUserPool/virtualUserPool.table";
import { getVirtualUsersTableActions } from "../virtualUsers/virtualUsers.actions";
import { getVirtualUserFormOptions } from "../virtualUsers/virtualUsers.selectors";
import { VIRTUAL_USERS_TABLES_CONFIG } from "../virtualUsers/virtualUsers.table";

type SchedulerFlowState = {
  schedulerJobMappingItems: SchedulerJobMapping[];
  schedulerJobMappingItemsSelected: number[];
  schedulerJobMappingItemsSelectedMap: PlainObject<number>;
  isDraggable: boolean;
  selectedTab: string;
  isLoadingSchedulerJobMappingItems: boolean;
  storageOptions: CascaderOptionType[];
};

type SchedulerFlowProps = {
  schedulerJobId: number;
};

const initialState = {
  schedulerJobMappingItems: [],
  schedulerJobMappingItemsSelected: [],
  schedulerJobMappingItemsSelectedMap: {},
  isDraggable: false,
  selectedTab: "scripts",
  isLoadingSchedulerJobMappingItems: false,
  storageOptions: [],
};

const Container = styled.div({
  display: "flex",
  flexDirection: "column",
  flexWrap: "wrap",
});
const { confirm } = Modal;

class SchedulerFlowContainer extends React.Component<
  IConnectProps & InjectedIntlProps,
  SchedulerFlowState
> {
  state: SchedulerFlowState = initialState;
  editAreaContainer: any;

  componentDidMount() {
    this.loadStorages();
    this.props.itsConfigurations.load({});
    this.props.virtualUsersActions.load({});
    this.props.environmentsActions.load({});
    this.props.virtualUserPoolActions.load({});
  }

  componentWillReceiveProps(nextProps: IConnectProps & { schedulerJobId: number }) {
    if (this.props.schedulerJobId !== nextProps.schedulerJobId) {
      this.loadStorages();
      this.props.itsConfigurations.load({});
    }
  }

  loadStorages = async () => {
    const result = await API.getStoragesWithContainers({});
    const storageOptions = createStorageSelectOptions(result);
    this.setState({ storageOptions });
  };

  saveAndLoadScheduler = async (job) => {
    await API.editSchedulerJob({
      ...schedulerFormValuesToJob(job),
      id: this.props.schedulerJobId,
      status: JOB_STATUS_TYPE.INACTIVE,
    });
    this.props.loadSingle({ id: this.props.schedulerJobId });
  };

  onSave = async (job) => {
    const { intl, schedulerJob, schedulerJobId, loadSingle } = this.props;

    if (schedulerJob.status === JOB_STATUS_TYPE.CLOSED) {
      confirm({
        title: intl.formatMessage({
          id: getTranslationKey("messages", "confirm", "reactivateScheduler"),
        }),
        okText: intl.formatMessage({ id: getTranslationKey("button", "yes") }),
        okType: "danger",
        cancelText: intl.formatMessage({ id: getTranslationKey("button", "cancel") }),
        onOk: async () => {
          await this.saveAndLoadScheduler(job);
        },
        onCancel: () => {
          loadSingle({ id: schedulerJobId });
        },
        okButtonProps: { [`${DataTestIdProp}`]: DataTestIds.MODAL_BUTTON_YES } as any,
        cancelButtonProps: { [`${DataTestIdProp}`]: DataTestIds.MODAL_BUTTON_CANCEL } as any,
      });
    } else {
      await this.saveAndLoadScheduler(job);
    }
  };

  onCancel = () => {};

  render() {
    const {
      schedulerJob,
      configurationOptions,
      environmentOptions,
      virtualUserOptions,
      virtualUserPoolOptions,
    } = this.props;
    const { storageOptions } = this.state;

    return (
      <Container style={{ width: "550px" }}>
        <EditeableAreaFinal
          onSave={this.onSave}
          disableEditing={schedulerJob.isInternal || schedulerJob.status === JOB_STATUS_TYPE.ACTIVE}
          initialValues={jobToFormValues(schedulerJob)}
          onCancel={this.onCancel}
          render={({ change, values }) => (
            <CreateSchedulerJobForm
              storageOptions={storageOptions}
              values={values}
              change={change}
              configurationOptions={configurationOptions}
              environmentOptions={environmentOptions}
              virtualUserOptions={virtualUserOptions}
              virtualUserPoolOptions={virtualUserPoolOptions}
            />
          )}
        />
      </Container>
    );
  }
}

const mapDispatchToProps = (dispatch: Dispatch) => ({
  ...bindActionCreators(schedulerActions, dispatch),
  itsConfigurations: {
    ...bindActionCreators(
      getItsConfigurationsTableActions(ITS_CONFIGURATIONS_TABLES_CONFIG.ALL.id()),
      dispatch,
    ),
  },
  virtualUsersActions: {
    load: bindActionCreators(
      getVirtualUsersTableActions(VIRTUAL_USERS_TABLES_CONFIG.ALL.id()),
      dispatch,
    ).load,
  },
  environmentsActions: {
    load: bindActionCreators(
      getSystemTableActions(SYSTEM_DICTIONARY_TABLES_CONFIG.ALL.id()),
      dispatch,
    ).load,
  },
  virtualUserPoolActions: {
    load: bindActionCreators(
      getVirtualUserPoolTableActions(VIRTUAL_USER_POOL_TABLES_CONFIG.ALL.id()),
      dispatch,
    ).load,
  },
});

const connectCreator = connect(
  (state: ApplicationState, props: RouteComponentProps<any> & SchedulerFlowProps) => ({
    ...props,
    schedulerJob: schedulerDataSelectors.getItemSelector(state, props.match.params.id),
    configurationOptions: getItsConfigurationOptions(
      state,
      ITS_CONFIGURATIONS_TABLES_CONFIG.ALL.id(),
    ),
    environmentOptions: getSystemFormOptions(state, SYSTEM_DICTIONARY_TABLES_CONFIG.ALL.id()),
    virtualUserOptions: getVirtualUserFormOptions(state, VIRTUAL_USERS_TABLES_CONFIG.ALL.id()),
    virtualUserPoolOptions: getVirtualUserPoolsFormOptions(
      state,
      VIRTUAL_USER_POOL_TABLES_CONFIG.ALL.id(),
    ),
  }),
  mapDispatchToProps,
);

type IConnectProps = ConnectedProps<typeof connectCreator>;

export default withRouter(connectCreator(injectIntl(SchedulerFlowContainer)));
